import { createReducer, isAnyOf } from '@reduxjs/toolkit';
import { eventRoomActions } from 'pages/EventRoom/state';
import { agoraActions } from 'store/features/agora';

import { captionsActions } from './captions.actions';
import { CAPTION_SPEAKER_COLORS, MAX_FINAL_CAPTION_LINES } from './captions.constants';
import { type CaptionsState } from './types';

const initialState: CaptionsState = {
  isRttActive: false,
  showCaptions: false,
  hasError: false,
  lines: [],
  speakerColorByUid: {},
};

export const captionsReducer = createReducer(initialState, (builder) => {
  builder
    .addCase(captionsActions.rttRequested, (state) => {
      state.isRttActive = true;
      // A successful start clears any error from a previous attempt.
      state.hasError = false;
    })
    .addCase(captionsActions.captionsFailed, (state) => {
      // Captions couldn't start: surface the error in the (still-open) region; don't close captions.
      state.hasError = true;
    })
    .addCase(captionsActions.captionsShown, (state, action) => {
      state.showCaptions = action.payload;
      // Turning captions off clears the error state.
      if (!action.payload) {
        state.hasError = false;
      }
    })
    .addCase(captionsActions.captionLineReceived, (state, action) => {
      const incoming = action.payload;
      const lastLine = state.lines.at(-1);

      // Any caption arriving means captions are working — clear a lingering error.
      state.hasError = false;

      // Assign the speaker a stable colour by order of appearance, so different
      // speakers get different colours and a speaker keeps theirs all session.
      if (incoming.speakerId != null && state.speakerColorByUid[incoming.speakerId] === undefined) {
        const nextIndex = Object.keys(state.speakerColorByUid).length % CAPTION_SPEAKER_COLORS.length;
        state.speakerColorByUid[incoming.speakerId] = nextIndex;
      }

      // A caption replaces a trailing interim line so the "live" line updates in place
      // instead of the list growing unbounded — but only when it's the *same* speaker.
      // In cross-talk, a different speaker's incoming line must not clobber the previous
      // speaker's in-flight partial; it starts a new line instead.
      if (lastLine && !lastLine.isFinal && lastLine.speakerId === incoming.speakerId) {
        state.lines[state.lines.length - 1] = incoming;
      } else {
        state.lines.push(incoming);
      }

      // Keep at most the last MAX_FINAL_CAPTION_LINES final lines (plus a single
      // trailing interim line, if present). Drop the oldest final lines beyond the cap.
      const trailingInterim = !state.lines.at(-1)?.isFinal ? state.lines.at(-1) : undefined;
      const finalLines = state.lines.filter((line) => line.isFinal);

      if (finalLines.length > MAX_FINAL_CAPTION_LINES) {
        const keptFinalLines = finalLines.slice(-MAX_FINAL_CAPTION_LINES);
        state.lines = trailingInterim ? [...keptFinalLines, trailingInterim] : keptFinalLines;
      }
    })
    // Leaving the event entirely, or an explicit stop/clear, fully resets — CC turns off.
    .addCase(eventRoomActions.cleanedUp, () => initialState)
    .addMatcher(isAnyOf(captionsActions.rttStopped, captionsActions.cleared), () => initialState)
    // Moving between broadcast and a nattering room clears the room-specific transcript/RTT state,
    // but preserves the user's CC preference so captions auto-resume in the room they move into.
    .addMatcher(isAnyOf(agoraActions.cleanedUpAfterBroadcast, agoraActions.cleanedUpAfterNattering), (state) => ({
      ...initialState,
      showCaptions: state.showCaptions,
    }));
});
